import { HTMLAttributes, ReactNode } from "react";

type CardVariant = "default" | "sticker" | "polaroid" | "glass";
type CardPadding = "none" | "sm" | "md" | "lg";

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  hoverable?: boolean;
  children: ReactNode;
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  children?: ReactNode;
}

interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

const variantStyles: Record<CardVariant, string> = {
  default: "bg-[var(--color-surface)] border border-[var(--color-border)] shadow-sm",
  sticker: "sticker-card",
  polaroid: "polaroid-frame p-4",
  glass: "glass-panel rounded-xl transition-all",
};

const paddingStyles: Record<CardPadding, string> = {
  none: "p-0",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

export function Card({
  variant = "default",
  padding = "md",
  hoverable = false,
  className = "",
  children,
  ...props
}: CardProps) {
  // Apply padding to default and glass variants
  const shouldApplyPadding = variant === "default" || variant === "glass";

  return (
    <div
      className={`rounded-xl transition-all ${variantStyles[variant]} ${shouldApplyPadding ? paddingStyles[padding] : ""} ${hoverable ? "hover:shadow-md hover:-translate-y-1 cursor-pointer" : ""} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  className = "",
  children,
  ...props
}: CardHeaderProps) {
  return (
    <div
      className={`flex items-start justify-between gap-4 mb-4 ${className}`}
      {...props}
    >
      <div className="flex-1">
        {title && (
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">{title}</h3>
        )}
        {subtitle && (
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{subtitle}</p>
        )}
        {children}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

export function CardBody({ className = "", children, ...props }: CardBodyProps) {
  return (
    <div className={`text-[var(--color-text-secondary)] ${className}`} {...props}>
      {children}
    </div>
  );
}

export function CardFooter({
  className = "",
  children,
  ...props
}: CardFooterProps) {
  return (
    <div
      className={`mt-4 pt-4 border-t border-[var(--color-border)] flex items-center justify-end gap-2 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export type { CardProps };
